import { OnChangeCache, processCondition } from '@msdk/common'
import {
  ClearLogicDefinition,
  EntityContext,
  EntityModel,
  FieldDefinition,
  FormModel,
  LogicalPath,
  LogicDefinition,
  SetIfEmptyLogicDefinition,
  SetLogicDefinition,
  TenantPath,
  UserPath,
} from '@msdk/definitions'

/**
 * The function "runOnChange" applies a set of actions based on certain conditions and changes in the
 * provided entity and form models.
 * @param {EntityContext} entity - The `entity` parameter is an object that represents the current
 * state of the entity being modified. It contains various properties and values that can be accessed
 * and manipulated.
 * @param {OnChangeCache} cache - The `cache` parameter is an object that contains cached functions
 * and rules for the `runOnChange` function. It is used to store and retrieve functions and rules that
 * are used to determine when and how actions should be applied based on changes in the entity or
 * form.
 * @param {string} fieldChanged - The `fieldChanged` parameter is a string that represents the name of
 * the field that was changed.
 * @param {EntityModel} entityModel - The `entityModel` parameter represents the model of the entity
 * being changed. It contains information about the structure and properties of the entity.
 * @param {FormModel} formModel - The `formModel` parameter represents the model of the form that is
 * being used. It contains information about the fields in the form, their values, and any validation
 * rules associated with them.
 */
export function runOnChange(
  entity: EntityContext,
  cache: OnChangeCache,
  fieldChanged: string,
  entityModel: EntityModel,
  formModel: FormModel,
) {
  cache.rules.forEach((r) => {
    const func = cache.cachedFunctions[processCondition(r.condition)]
    const changeOnField = r.changeOnFieldList && r.changeOnFieldList.includes(fieldChanged)
    const onChangeEmpty = !r.changeOnFieldList ?? r.changeOnFieldList.length === 0
    const funcResult = func({ entity })
    // possible combinations are:
    // applyAlways: the rule will always be applied every onChange
    // changeOnField and function: the rule will only be applied when the given fields change and the expression returns true
    // func: the rule will only be applied when the funciton returns true
    if (r.applyAlways || (changeOnField && funcResult) || (onChangeEmpty && funcResult)) {
      r.actions.forEach((a) => {
        applyActions(a, entity, cache, entityModel, formModel)
      })
    }
  })
}
/**
 * The function `applyActions` takes in various parameters and performs different actions based on the
 * logic type specified in the `action` parameter.
 * @param {LogicDefinition} action - The `action` parameter is of type `LogicDefinition` and represents
 * the logic action to be applied.
 * @param {EntityContext} entity - The `entity` parameter represents the current state of an entity. It
 * contains information about the fields and their values.
 * @param {OnChangeCache} cache - The `cache` parameter is an object that contains cached functions.
 * These functions are used to evaluate the value of a field in the `applyActions` function.
 * @param {EntityModel} entityModel - The `entityModel` parameter represents the model of the entity
 * that contains the fields and their definitions. It provides information about the fields such as
 * their names, types, and validation rules.
 * @param {FormModel} formModel - The `formModel` parameter is an object that represents the model of a
 * form. It contains information about the fields in the form, such as their names, types, and values.
 */
function applyActions(
  action: LogicDefinition,
  entity: EntityContext,
  cache: OnChangeCache,
  entityModel: EntityModel,
  formModel: FormModel,
) {
  switch (action.logicType) {
    case 'Set': {
      const setDef = action as SetLogicDefinition
      const func = cache.cachedFunctions[setDef.value]

      let fieldDef = entityModel.getFieldDefinition(setDef.field)
      if (!fieldDef && setDef.field.includes('.')) {
        const fName = setDef.field.split('.')[1]
        fieldDef = formModel.fields.find((f) => f.name === fName) as FieldDefinition
      }

      const value = evaluate(entity, func)
      const fieldValue = fieldDef.getFieldValue()
      fieldValue.value = value
      entity.fields[setDef.field] = fieldValue
      break
    }
    case 'SetIfEmpty': {
      const setDef = action as SetIfEmptyLogicDefinition
      const currentValue = entity.fields[setDef.field]
      if (!currentValue || currentValue.isEmpty()) {
        const func = cache.cachedFunctions[setDef.value]

        let fieldDef = entityModel.getFieldDefinition(setDef.field)
        if (!fieldDef && setDef.field.includes('.')) {
          const fName = setDef.field.split('.')[1]
          fieldDef = formModel.fields.find((f) => f.name === fName) as FieldDefinition
        }

        const value = evaluate(entity, func)
        const fieldValue = fieldDef.getFieldValue()
        fieldValue.value = value
        entity.fields[setDef.field] = fieldValue
      }
      break
    }
    case 'Clear': {
      const clearDef = action as ClearLogicDefinition
      clearDef.field
        .split(',')
        .forEach((field) => {
          entity.fields[field] = null
        })
      break
    }
    default:
      break
  }
}

/**
 * The evaluate function takes a context, a function, and an optional row context, and returns the
 * result of calling the function with the provided contexts.
 * @param {EntityContext} context - The `context` parameter represents the entity context, which
 * contains information about the current entity being evaluated.
 * @param {Function} func - The `func` parameter is a function that takes in an object with properties
 * `entity`, `row`, and `logic`. It is expected to return a boolean value.
 * @param {EntityContext} [rowContext] - The `rowContext` parameter is an optional parameter that
 * represents the context of a specific row in a dataset. It can be used to provide additional
 * information or context specific to that row when evaluating the function.
 * @returns a boolean value.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function evaluate(context: EntityContext, func: Function, rowContext?: EntityContext): boolean {
  const res = func({
    entity: context,
    row: rowContext,
    logic: new LogicalPath(),
    user: new UserPath(),
    tenant: new TenantPath(),
  })
  return res
}
